// StaffRewardsPage — driver / dispatcher / super-admin facing.
//   /staff/rewards
// Shows the staff member's QR code (for in-person customer onboarding),
// their staff_points_ledger balance + history, and a leaderboard preview
// when relevant (super-admin sees full leaderboard via /loyalty/staff-ledger).
//
// Staff earn:
//   - 50 pts when a customer registers with their code
//   - 100 pts when that customer completes their first delivered order
//
// Server: GET /api/staff/me/qr, GET /api/staff/me/points, GET /api/loyalty/staff-ledger.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Copy, Share2, ArrowLeft, QrCode, Trophy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFlag } from '../../context/FeatureFlagsContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const ROLE_BADGE_BG = {
  driver: '#39FF14',
  dispatcher: '#0AC2FF',
  super_admin: '#FFA500',
};

export function StaffRewardsPage() {
  const { user } = useAuth();
  const enabled = useFlag('loyalty.staff_compensation', true);
  const [qr, setQR] = useState(null);
  const [points, setPoints] = useState({ balance: 0, history: [] });
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (!['driver', 'dispatcher', 'super_admin'].includes(user.role)) return;
    let cancelled = false;
    async function load() {
      try {
        const [qrRes, ptRes] = await Promise.all([
          axios.get(`${API_URL}/api/staff/me/qr`, { withCredentials: true }),
          axios.get(`${API_URL}/api/staff/me/points`, { withCredentials: true }),
        ]);
        if (!cancelled) {
          setQR(qrRes.data);
          setPoints(ptRes.data);
        }
        if (user.role === 'super_admin') {
          try {
            const lb = await axios.get(`${API_URL}/api/loyalty/staff-ledger`, { withCredentials: true });
            if (!cancelled) setLeaderboard(lb.data);
          } catch {/* non-fatal */}
        }
      } catch (e) {
        if (!cancelled) toast.error('Could not load staff rewards');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  if (!user || !['driver', 'dispatcher', 'super_admin'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-[#050003] text-[#39FF14] font-mono p-6">
        <p className="text-[#6B8E73]">// Staff only.</p>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="min-h-screen bg-[#050003] text-[#39FF14] font-mono p-6">
        <div className="max-w-2xl mx-auto pt-12 text-center">
          <p className="text-[#4B6B52]">// Staff compensation is currently disabled.</p>
        </div>
      </div>
    );
  }

  const code = qr?.code || '...';
  const dashboardHome = user.role === 'driver' ? '/driver' : '/dispatch';
  const shareUrl = `${window.location.origin}/register?staff=${code}`;
  const accent = ROLE_BADGE_BG[user.role] || '#39FF14';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('QR link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Ash & Merch signup', url: shareUrl }); } catch {}
    } else {
      handleCopy();
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] text-[#39FF14] font-mono" data-testid="staff-rewards-page">
      <div
        className="border-b border-[#2A0F3D] sticky top-0 z-10 bg-[#050003]/95 backdrop-blur"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={dashboardHome} className="text-[#6B8E73] hover:text-[#39FF14] flex items-center gap-1 text-xs uppercase" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> BACK
          </Link>
          <span className="text-[#39FF14] font-bold tracking-wider">{'>'} STAFF_REWARDS_</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* QR card */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6 flex flex-col md:flex-row gap-6">
          <div className="bg-white p-3 self-center md:self-start" data-testid="staff-qr">
            <QRCodeSVG value={shareUrl} size={180} bgColor="#FFFFFF" fgColor="#050003" level="M" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-[#4B6B52] uppercase tracking-wider mb-1">// YOUR ONBOARDING CODE</p>
            <div className="text-3xl font-bold tracking-[0.3em] mb-2 neon-text" data-testid="staff-code"
              style={{ color: accent }}>{code}</div>
            <p className="text-sm text-[#6B8E73] mb-3 max-w-md">
              // Show this QR or share the link with potential customers. You earn 50 pts when they sign up,
              and 100 pts more when they complete their first delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                readOnly
                value={shareUrl}
                data-testid="staff-share-url"
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono text-xs rounded-none"
              />
              <div className="flex gap-2">
                <Button onClick={handleCopy} data-testid="staff-copy-btn" variant="outline"
                  className="border-[#39FF14] text-[#39FF14] hover:bg-[#2A0F3D] rounded-none">
                  <Copy className="w-4 h-4 mr-1" /> COPY
                </Button>
                <Button onClick={handleShare} data-testid="staff-share-btn"
                  className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none">
                  <Share2 className="w-4 h-4 mr-1" /> SHARE
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Balance */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-[#4B6B52] uppercase tracking-wider">// STAFF REWARDS BALANCE</p>
            <p className="text-3xl font-bold mt-1" data-testid="staff-balance" style={{ color: accent }}>
              {points.balance} PTS
            </p>
          </div>
          <QrCode className="w-12 h-12 opacity-30" />
        </div>

        {/* History */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
          <p className="text-xs text-[#4B6B52] uppercase tracking-wider mb-3">// REWARDS HISTORY</p>
          {loading ? (
            <p className="text-[#6B8E73] text-sm">Loading...</p>
          ) : points.history.length === 0 ? (
            <div className="text-center py-8 text-[#6B8E73]" data-testid="staff-no-history">
              // No rewards yet. Onboard your first customer to start earning.
            </div>
          ) : (
            <ul className="divide-y divide-[#2A0F3D]">
              {points.history.map((entry) => (
                <li key={entry.id} className="py-3 flex items-center justify-between" data-testid={`staff-history-${entry.id}`}>
                  <div className="text-sm">
                    <p className="text-[#39FF14]">{entry.reason}</p>
                    <p className="text-[#4B6B52] text-xs">
                      {entry.source.toUpperCase()} · {new Date(entry.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span className="text-lg font-bold" style={{ color: entry.delta > 0 ? '#39FF14' : '#FF3B30' }}>
                    {entry.delta > 0 ? '+' : ''}{entry.delta}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Leaderboard for super-admin only */}
        {user.role === 'super_admin' && leaderboard && (
          <div className="bg-[#0C0612] border border-[#FFA500] p-4" data-testid="staff-leaderboard">
            <p className="text-xs text-[#FFA500] uppercase tracking-wider mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4" /> // SUPER-ADMIN: ALL STAFF LEADERBOARD
            </p>
            {leaderboard.totals.length === 0 ? (
              <p className="text-[#6B8E73] text-sm">// No rewards earned by any staff yet.</p>
            ) : (
              <ul className="divide-y divide-[#2A0F3D]">
                {leaderboard.totals.map((row, idx) => (
                  <li key={row.user_id} className="py-2 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-[#FFA500] w-6 text-right">#{idx + 1}</span>
                      <div>
                        <p className="text-[#39FF14]">{row.name || row.email}</p>
                        <p className="text-[#4B6B52] text-xs">{row.role?.toUpperCase()} · {row.region?.toUpperCase() || 'GLOBAL'}</p>
                      </div>
                    </div>
                    <span className="text-[#39FF14] font-bold">{row.balance} PTS</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffRewardsPage;
